import { useEffect, useState, type FormEvent } from "react";
import { ArrowRight, ExternalLink, KeyRound, Mail } from "lucide-react";
import type { CloudAuthConfig } from "@orni/sdk-typescript";
import { useI18n } from "../i18n";
import type { AuthPort } from "../auth-port";

type LoginMethod = "email_code" | "password" | "reset_password";
const EMAIL_CODE_RESEND_COOLDOWN_SECONDS = 60;

export function DesktopLoginForm({
  auth,
  onSuccess,
}: {
  auth: AuthPort;
  onSuccess: () => void;
}) {
  const { t } = useI18n();
  const [capabilities, setCapabilities] = useState<CloudAuthConfig | null>(null);
  const [method, setMethod] = useState<LoginMethod>("email_code");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [resetVerified, setResetVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [browserBusy, setBrowserBusy] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setResendSeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  useEffect(() => {
    let active = true;
    auth.capabilities()
      .then((value) => { if (active) setCapabilities(value); })
      .catch((cause) => {
        if (active) setError(cause instanceof Error ? cause.message : t("app.auth.unavailable"));
      });
    return () => { active = false; };
  }, [auth, t]);

  const requestPurpose = method === "reset_password" ? "reset_password" : "sign_in";
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (method === "password") {
        await auth.loginWithPassword({ email, password });
        onSuccess();
      } else if (method === "reset_password") {
        if (!challengeId) {
          const challenge = await auth.requestEmailCode(email, requestPurpose);
          setChallengeId(challenge.challengeId);
          setResendSeconds(EMAIL_CODE_RESEND_COOLDOWN_SECONDS);
        } else if (!resetVerified) {
          await auth.verifyPasswordReset({ challengeId, email, code });
          setResetVerified(true);
          setCode("");
          setNotice(t("app.auth.codeVerified"));
        } else {
          if (password !== confirmPassword) throw new Error(t("app.auth.passwordMismatch"));
          await auth.completePasswordReset({ password });
          onSuccess();
        }
      } else if (!challengeId) {
        const challenge = await auth.requestEmailCode(email);
        setChallengeId(challenge.challengeId);
        setResendSeconds(EMAIL_CODE_RESEND_COOLDOWN_SECONDS);
      } else {
        await auth.verifyEmailCode({ challengeId, email, code });
        onSuccess();
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("app.auth.unavailable"));
      if (isRateLimitedError(cause)) setResendSeconds(EMAIL_CODE_RESEND_COOLDOWN_SECONDS);
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (busy || !challengeId || resendSeconds > 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const challenge = await auth.requestEmailCode(email, requestPurpose);
      setChallengeId(challenge.challengeId);
      setCode("");
      setResendSeconds(EMAIL_CODE_RESEND_COOLDOWN_SECONDS);
      setNotice(t("app.auth.codeResent"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("app.auth.unavailable"));
      if (isRateLimitedError(cause)) setResendSeconds(EMAIL_CODE_RESEND_COOLDOWN_SECONDS);
    } finally {
      setBusy(false);
    }
  };

  const switchMethod = (next: LoginMethod) => {
    setMethod(next);
    setChallengeId(null);
    setResetVerified(false);
    setCode("");
    setPassword("");
    setConfirmPassword("");
    setResendSeconds(0);
    setNotice(null);
    setError(null);
  };
  const ready = capabilities && (method === "password" ? capabilities.password : capabilities.emailCode);
  const openBrowserLogin = async () => {
    if (browserBusy) return;
    setBrowserBusy(true);
    setError(null);
    try {
      await auth.openBrowserLogin();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("app.auth.unavailable"));
    } finally {
      setBrowserBusy(false);
    }
  };

  return (
    <div className="desktop-login-form">
      <button className="desktop-browser-login" type="button" onClick={() => void openBrowserLogin()} disabled={browserBusy}>
        <span>
          <strong>{t("app.auth.browserSignIn")}</strong>
          <small>{t("app.auth.browserSignInDetail")}</small>
        </span>
        <ExternalLink size={16} />
      </button>
      <div className="desktop-login-divider"><span>{t("app.auth.orSignInHere")}</span></div>
      {method === "reset_password" ? (
        <div className="desktop-login-reset-heading">
          <button type="button" onClick={() => switchMethod("password")}>{t("app.auth.backToSignIn")}</button>
          <strong>{t("app.auth.resetPassword")}</strong>
        </div>
      ) : (
        <div className="desktop-login-methods" role="tablist" aria-label={t("app.auth.method")}>
          <button type="button" role="tab" aria-selected={method === "email_code"} className={method === "email_code" ? "active" : ""} onClick={() => switchMethod("email_code")}>
            <Mail size={15} /> {t("app.auth.emailCode")}
          </button>
          <button type="button" role="tab" aria-selected={method === "password"} className={method === "password" ? "active" : ""} onClick={() => switchMethod("password")}>
            <KeyRound size={15} /> {t("app.auth.password")}
          </button>
        </div>
      )}
      <form onSubmit={submit}>
        <label>
          <span>{t("app.auth.email")}</span>
          <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t("app.auth.emailPlaceholder")} autoComplete="email" required disabled={Boolean(challengeId) || busy} autoFocus />
        </label>
        {method === "password" ? (
          <label>
            <span>{t("app.auth.password")}</span>
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t("app.auth.passwordPlaceholder")} autoComplete="current-password" minLength={10} required disabled={busy} />
            <button type="button" className="desktop-login-forgot" onClick={() => switchMethod("reset_password")}>{t("app.auth.forgotPassword")}</button>
          </label>
        ) : method === "reset_password" && resetVerified ? (
          <>
            <label>
              <span>{t("app.auth.newPassword")}</span>
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t("app.auth.passwordPlaceholder")} autoComplete="new-password" minLength={10} required disabled={busy} />
            </label>
            <label>
              <span>{t("app.auth.confirmPassword")}</span>
              <input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder={t("app.auth.confirmPasswordPlaceholder")} autoComplete="new-password" minLength={10} required disabled={busy} />
            </label>
          </>
        ) : null}
        {method === "reset_password" && challengeId && !resetVerified ? (
          <VerificationCodeField code={code} setCode={setCode} busy={busy} resendSeconds={resendSeconds} onResend={resendCode} t={t} />
        ) : method === "email_code" && challengeId ? (
          <VerificationCodeField code={code} setCode={setCode} busy={busy} resendSeconds={resendSeconds} onResend={resendCode} t={t} />
        ) : null}
        {notice ? <p className="desktop-login-notice" role="status">{notice}</p> : null}
        {error ? <p className="desktop-login-error" role="alert">{error}</p> : null}
        <button className="desktop-login-submit" type="submit" disabled={!ready || busy || ((method === "email_code" || method === "reset_password") && !challengeId && resendSeconds > 0)}>
          <span>{busy
            ? t("app.auth.signingIn")
            : method === "password"
              ? t("app.auth.passwordSignIn")
              : method === "reset_password"
                ? resetVerified ? t("app.auth.resetAndSignIn") : challengeId ? t("app.auth.verifyCode") : t("app.auth.sendResetCode")
                : challengeId
                  ? t("app.auth.verifyAndSignIn")
                  : resendSeconds > 0
                    ? t("app.auth.resendIn", { seconds: resendSeconds })
                    : t("app.auth.sendCode")}</span>
          <ArrowRight size={16} />
        </button>
      </form>
    </div>
  );
}

function VerificationCodeField({
  code,
  setCode,
  busy,
  resendSeconds,
  onResend,
  t,
}: {
  code: string;
  setCode: (value: string) => void;
  busy: boolean;
  resendSeconds: number;
  onResend: () => void;
  t: ReturnType<typeof useI18n>["t"];
}) {
  return (
    <label>
      <span>{t("app.auth.verificationCode")}</span>
      <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder={t("app.auth.codePlaceholder")} autoComplete="one-time-code" required disabled={busy} autoFocus />
      <div className="desktop-login-code-actions">
        <small>{t("app.auth.codeSent")}</small>
        <button type="button" className="desktop-login-resend" onClick={onResend} disabled={busy || resendSeconds > 0}>
          {resendSeconds > 0 ? t("app.auth.resendIn", { seconds: resendSeconds }) : t("app.auth.resendCode")}
        </button>
      </div>
    </label>
  );
}

function isRateLimitedError(cause: unknown): boolean {
  return cause instanceof Error && /too recently|rate.?limited|429/i.test(cause.message);
}
